type UnknownRecord = Record<string, unknown>;

const SENSITIVE_FIELDS = new Set([
  'password',
  'currentpassword',
  'newpassword',
  'confirmpassword',
  'token',
  'accesstoken',
  'refreshtoken',
  'csrftoken',
  'idtoken',
  'secret',
  'apikey',
  'authorization',
  'cookie',
  'setcookie',
  'clientsecret',
  'stripesecretkey',
  'paymentmethod',
  'paymentmethodid',
  'source',
  'card',
  'cardnumber',
  'cvv',
  'cvc',
  'ssn',
  'accountnumber',
  'routingnumber',
  'accountholdername',
  'pan',
  'email',
  'phone',
  'phonenumber',
  'firstname',
  'lastname',
  'fullname',
  'address',
]);

function normalizeKey(key: string): string {
  return key.toLowerCase().replace(/[_\-\s]/g, '');
}

export function normalizeSentryEnvironment(
  value: unknown
): 'development' | 'staging' | 'production' {
  const normalized = String(value || '').trim().toLowerCase();
  if (['main', 'prod', 'production'].includes(normalized)) return 'production';
  if (['staging', 'stage', 'preview'].includes(normalized)) return 'staging';
  return 'development';
}

export function isSensitiveSentryKey(key: string): boolean {
  return SENSITIVE_FIELDS.has(normalizeKey(key));
}

export function scrubSentryString(value: unknown): string {
  return String(value)
    .replace(/Bearer\s+[A-Za-z0-9._~+/=-]+/gi, 'Bearer [REDACTED]')
    .replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, '[REDACTED_EMAIL]')
    .replace(/\b(?:\d[ -]*?){13,19}\b/g, '[REDACTED_NUMBER]')
    .replace(/\b[a-f\d]{24}\b/gi, '[REDACTED_ID]')
    .replace(/\b[0-9a-f]{8}-[0-9a-f-]{27,36}\b/gi, '[REDACTED_ID]');
}

export function scrubSentryValue(
  value: unknown,
  depth = 0,
  seen = new WeakSet<object>()
): unknown {
  if (value === null || value === undefined) return value;
  if (typeof value === 'string') return scrubSentryString(value);
  if (typeof value !== 'object') return value;
  if (depth > 6) return '[TRUNCATED]';
  if (seen.has(value)) return '[CIRCULAR]';

  seen.add(value);
  if (Array.isArray(value)) {
    return value.slice(0, 50).map((item) => scrubSentryValue(item, depth + 1, seen));
  }

  const output: UnknownRecord = {};
  for (const [key, child] of Object.entries(value as UnknownRecord)) {
    output[key] = isSensitiveSentryKey(key)
      ? '[REDACTED]'
      : scrubSentryValue(child, depth + 1, seen);
  }
  return output;
}

export function scrubSentryEvent<T extends object>(event: T): T {
  const safeEvent = scrubSentryValue(event) as UnknownRecord;
  delete safeEvent.user;
  delete safeEvent.extra;

  const request = safeEvent.request as UnknownRecord | undefined;
  if (request) {
    if (typeof request.url === 'string') {
      request.url = scrubSentryString(request.url.split('?')[0]);
    }
    delete request.data;
    delete request.cookies;
    delete request.query_string;
    delete request.headers;
  }

  return safeEvent as T;
}

export function scrubSentryBreadcrumb<T extends object>(breadcrumb: T): T | null {
  const record = breadcrumb as UnknownRecord;
  const category = String(record.category || '');
  if (category === 'console' || category === 'ui.input' || category === 'ui.click') return null;
  return scrubSentryValue(breadcrumb) as T;
}
